import React, { useEffect, useState } from 'react'
import { usePackageStore } from '@store'
import { Plus, Eye, Edit2, Trash2, Archive, RotateCcw, DollarSign, Package, TrendingUp, Filter, ArrowUpDown } from 'lucide-react'
import { DataTable, SearchBar, Select, Button, ConfirmDialog, PageHeader } from '@components/common'
import PackageForm from './PackageForm'
import PackageView from './PackageView'
import './Packages.css'

/**
 * Packages Management Page (Super Admin Only)
 *
 * Complete package management interface with:
 * - List view with DataTable
 * - Search and filtering
 * - Create/Edit/View/Delete operations
 * - Statistics dashboard
 * - Pagination
 * - Sorting
 * - Responsive design
 *
 * Features:
 * - Full CRUD operations
 * - Real-time search
 * - Status filtering
 * - Billing cycle filtering
 * - Soft delete with restore
 * - Confirmation dialogs
 * - Loading states
 * - Empty states
 * - Error handling
 */
const Packages = () => {
	const {
		packages,
		loading,
		filters,
		pagination,
		stats,
		fetchPackages,
		selectPackage,
		archivePackage,
		deletePackage,
		restorePackage,
		setSearch,
		setStatusFilter,
		setBillingCycleFilter,
		setPage,
		nextPage,
		prevPage,
	} = usePackageStore()

	// Modal states
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showEditModal, setShowEditModal] = useState(false)
	const [showViewModal, setShowViewModal] = useState(false)
	const [showConfirmDialog, setShowConfirmDialog] = useState(false)
	const [actionType, setActionType] = useState(null) // 'archive', 'delete', 'restore'
	const [selectedPackageForAction, setSelectedPackageForAction] = useState(null)

	// Fetch packages on mount
	useEffect(() => {
		fetchPackages()
	}, [])

	/**
	 * Handle create package
	 */
	const handleCreate = () => {
		selectPackage(null)
		setShowCreateModal(true)
	}

	/**
	 * Handle view package
	 */
	const handleView = (pkg) => {
		selectPackage(pkg)
		setShowViewModal(true)
	}

	/**
	 * Handle edit package
	 */
	const handleEdit = (pkg) => {
		selectPackage(pkg)
		setShowEditModal(true)
	}

	/**
	 * Handle archive package
	 */
	const handleArchive = (pkg) => {
		setSelectedPackageForAction(pkg)
		setActionType('archive')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle delete package
	 */
	const handleDelete = (pkg) => {
		setSelectedPackageForAction(pkg)
		setActionType('delete')
		setShowConfirmDialog(true)
	}

	/**
	 * Handle restore package
	 */
	const handleRestore = (pkg) => {
		setSelectedPackageForAction(pkg)
		setActionType('restore')
		setShowConfirmDialog(true)
	}

	/**
	 * Confirm action (archive/delete/restore)
	 */
	const confirmAction = async () => {
		if (!selectedPackageForAction) return

		try {
			if (actionType === 'archive') {
				await archivePackage(selectedPackageForAction.id)
			} else if (actionType === 'delete') {
				await deletePackage(selectedPackageForAction.id)
			} else if (actionType === 'restore') {
				await restorePackage(selectedPackageForAction.id)
			}

			setShowConfirmDialog(false)
			setSelectedPackageForAction(null)
			setActionType(null)
		} catch (error) {
			console.error('Action failed:', error)
		}
	}

	/**
	 * Cancel action
	 */
	const cancelAction = () => {
		setShowConfirmDialog(false)
		setSelectedPackageForAction(null)
		setActionType(null)
	}

	/**
	 * Format currency
	 */
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
		}).format(amount)
	}

	/**
	 * Format storage
	 */
	const formatStorage = (bytes) => {
		return (bytes / (1024 * 1024 * 1024)).toFixed(0) + ' GB'
	}

	/**
	 * Get status badge
	 */
	const getStatusBadge = (status, deletedAt) => {
		if (deletedAt || status === 'archived') {
			return <span className="badge badge--archived">Archived</span>
		}
		return <span className="badge badge--active">Active</span>
	}

	// Define table columns
	const columns = [
		{
			key: 'title',
			label: 'Package',
			sortable: true,
			render: (pkg) => (
				<div className="package-cell">
					<div className="package-cell__title">{pkg.title}</div>
					<div className="package-cell__slug">{pkg.slug}</div>
				</div>
			),
		},
		{
			key: 'price',
			label: 'Price',
			sortable: true,
			align: 'right',
			render: (pkg) => (
				<div className="price-cell">
					<div className="price-cell__amount">{formatCurrency(pkg.price)}</div>
					<div className="price-cell__cycle">
						{pkg.billing_cycle === 'yearly' ? 'per year' : 'per month'}
					</div>
				</div>
			),
		},
		{
			key: 'user_limit',
			label: 'User limit',
			sortable: true,
			align: 'center',
			render: (pkg) => (pkg.user_limit === -1 ? 'Unlimited' : pkg.user_limit),
		},
		{
			key: 'storage_limit',
			label: 'Storage',
			sortable: true,
			align: 'center',
			render: (pkg) => pkg.storage_limit_formatted || formatStorage(pkg.storage_limit),
		},
		{
			key: 'trial_days',
			label: 'Trial',
			sortable: true,
			align: 'center',
			render: (pkg) => `${pkg.trial_days} days`,
		},
		{
			key: 'status',
			label: 'Status',
			sortable: true,
			align: 'center',
			render: (pkg) => getStatusBadge(pkg.status, pkg.deleted_at),
		},
	]

	// Define table actions
	const actions = [
		{
			label: 'View Details',
			icon: <Eye size={16} />,
			onClick: handleView,
		},
		{
			label: 'Edit',
			icon: <Edit2 size={16} />,
			onClick: handleEdit,
			show: (pkg) => !pkg.deleted_at && pkg.status !== 'archived',
		},
		{
			label: 'Archive',
			icon: <Archive size={16} />,
			onClick: handleArchive,
			variant: 'warning',
			show: (pkg) => !pkg.deleted_at && pkg.status !== 'archived',
		},
		{
			label: 'Delete',
			icon: <Trash2 size={16} />,
			onClick: handleDelete,
			variant: 'danger',
			show: (pkg) => !pkg.deleted_at && pkg.status !== 'archived',
		},
		{
			label: 'Restore',
			icon: <RotateCcw size={16} />,
			onClick: handleRestore,
			variant: 'primary',
			show: (pkg) => pkg.deleted_at || pkg.status === 'archived',
		},
	]

	return (
		<div className="packages-page">
			{/* Page Header with Breadcrumbs */}
			<PageHeader
				title="Subscription Packages"
				description="Manage subscription plans, pricing, and features"
				breadcrumbs={[{ label: 'Packages' }]}
				actions={
					<Button variant="primary" onClick={handleCreate}>
						<Plus size={20} />
						<span>Add Package</span>
					</Button>
				}
			/>

			{/* Statistics */}
			<div className="stats-grid">
				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--primary">
						<Package size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Total Packages</div>
						<div className="stat-card__value">{stats.total}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--success">
						<TrendingUp size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Active Packages</div>
						<div className="stat-card__value">{stats.active}</div>
					</div>
				</div>

				<div className="stat-card">
					<div className="stat-card__icon stat-card__icon--secondary">
						<DollarSign size={24} />
					</div>
					<div className="stat-card__content">
						<div className="stat-card__label">Archived</div>
						<div className="stat-card__value">{stats.archived}</div>
					</div>
				</div>
			</div>

			{/* Filters & Search */}
			<div className="filters-section">
				<SearchBar
					value={filters.search}
					onChange={setSearch}
					placeholder="Search packages..."
					loading={loading}
				/>

				<div className="filters-section__divider">
					<Filter size={18} />
				</div>

				<div className="filters-section__controls">
					<Select
						value={filters.status}
						onChange={(e) => setStatusFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Status' },
							{ value: 'active', label: 'Active' },
							{ value: 'archived', label: 'Archived' },
						]}
					/>

					<div className="filters-section__sort-icon">
						<ArrowUpDown size={18} />
					</div>

					<Select
						value={filters.billing_cycle}
						onChange={(e) => setBillingCycleFilter(e.target.value)}
						options={[
							{ value: 'all', label: 'All Billing Cycles' },
							{ value: 'monthly', label: 'Monthly' },
							{ value: 'yearly', label: 'Yearly' },
						]}
					/>
				</div>
			</div>

			{/* Data Table */}
			<DataTable
				columns={columns}
				data={packages}
				actions={actions}
				loading={loading}
				emptyMessage="No packages found. Create your first package to get started."
				onRowClick={handleView}
			/>

			{/* Pagination */}
			{pagination.totalPages > 1 && (
				<div className="pagination">
					<div className="pagination__info">
						Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
					</div>
					<div className="pagination__controls">
						<Button
							variant="secondary"
							onClick={prevPage}
							disabled={pagination.page === 1 || loading}
							size="small"
						>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={nextPage}
							disabled={pagination.page >= pagination.totalPages || loading}
							size="small"
						>
							Next
						</Button>
					</div>
				</div>
			)}

			{/* Create Modal */}
			{showCreateModal && <PackageForm mode="create" onClose={() => setShowCreateModal(false)} />}

			{/* Edit Modal */}
			{showEditModal && <PackageForm mode="edit" onClose={() => setShowEditModal(false)} />}

			{/* View Modal */}
			{showViewModal && (
				<PackageView
					packageData={packages.find((p) => p.id === usePackageStore.getState().selectedPackage?.id)}
					onClose={() => setShowViewModal(false)}
					onEdit={() => {
						setShowViewModal(false)
						setShowEditModal(true)
					}}
				/>
			)}

			{/* Confirm Dialog (Archive/Delete/Restore) */}
			{showConfirmDialog && selectedPackageForAction && (
				<ConfirmDialog
					isOpen={true}
					onConfirm={confirmAction}
					onCancel={cancelAction}
					title={
						actionType === 'archive'
							? 'Archive Package?'
							: actionType === 'delete'
								? 'Delete Package?'
								: 'Restore Package?'
					}
					message={
						actionType === 'archive'
							? `Are you sure you want to archive "${selectedPackageForAction.title}"? This will mark it as archived but preserve all data.`
							: actionType === 'delete'
								? `Are you sure you want to delete "${selectedPackageForAction.title}"? This will archive it and mark it as deleted, but data will be preserved.`
								: `Are you sure you want to restore "${selectedPackageForAction.title}"? This will make it active and available for selection again.`
					}
					confirmText={actionType === 'archive' ? 'Archive' : actionType === 'delete' ? 'Delete' : 'Restore'}
					variant={actionType === 'archive' ? 'warning' : actionType === 'delete' ? 'danger' : 'info'}
				/>
			)}
		</div>
	)
}

export default Packages
